import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { GoBackLink } from '../GoBackLink'
import { Table } from 'react-bootstrap'
import { useStateValue } from '@State'
import { client } from '@Utils'

const platformNameMap = {
  blackboard: 'Blackboard',
  desire2learn: 'Brightspace',
  canvas: 'Canvas',
  moodle: 'Moodle',
  myed: 'MyEducator Platform',
  all: 'All Platforms',
}

// Canvas serves LTI 1.3 from a small set of global issuers rather than one per institution:
// canvas.instructure.com is production, canvas.test.instructure.com and
// canvas.beta.instructure.com are the non-production environments. Match that
// `.test.`/`.beta.` host segment specifically -- searching for "test" anywhere in the URL
// would also catch a legitimate production issuer such as test-college.instructure.com.
const CANVAS_NON_PRODUCTION_ISS = /\.(test|beta)\.instructure\.com/i

/**
 * Split Canvas issuers into the production and test sections this page renders.
 *
 * Every issuer lands in exactly one of the two buckets, so nothing can silently disappear
 * from the page the way an allowlist of ids would allow. `iss` is non-nullable in dserv
 * (lti13.models.Issuer.iss is TextField(null=False)), so the falsy guard is only
 * belt-and-braces; such a record would show under Production, where its empty Issuer cell
 * makes the misconfiguration visible rather than hiding it.
 *
 * Exported for testing.
 */
export const partitionCanvasIssuers = issuers => ({
  production: issuers.filter(p => !p.iss || !CANVAS_NON_PRODUCTION_ISS.test(p.iss)),
  test: issuers.filter(p => p.iss && CANVAS_NON_PRODUCTION_ISS.test(p.iss)),
})

const IssuerTable = ({ issuers, lmsCode }) => (
  <div className="table-responsive">
    <Table
      striped
      bordered
      className="table-sm"
      hover
      style={{ tableLayout: 'fixed' }}
    >
      <colgroup>
        <col style={{ width: '5%' }} />
        <col style={{ width: '20%' }} />
        <col style={{ width: '20%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '30%' }} />
        <col style={{ width: '15%' }} />
      </colgroup>
      <thead>
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Issuer</th>
          <th>Code</th>
          <th>Authorization URL</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {issuers.length === 0 ? (
          <tr>
            <td colSpan={6} className="text-muted">
              No issuers in this section.
            </td>
          </tr>
        ) : (
          issuers.map(platform => (
            <tr key={platform.id}>
              <td>{platform.id}</td>
              <td>{platform.data.name}</td>
              <td style={{ overflowWrap: 'break-word' }}>{platform.iss}</td>
              <td>{platform.data.product_family_code}</td>
              <td style={{ overflowWrap: 'break-word' }}>
                <p>OIDC: {platform.data.auth_url}</p>
                <p>JWT: {platform.data.token_url}</p>
              </td>
              <td>
                <Link
                  to={`/account/admin_lti13/-/platforms/${lmsCode}/issuers/${platform.id}`}
                >
                  View Issuer
                </Link>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
  </div>
)

export const Platforms = ({ match }) => {
  const [currentState, dispatch] = useStateValue()
  useEffect(() => {
    const getPlatforms = async () => {
      try {
        const lmsCode = match.params.lmsCode ? match.params.lmsCode : 'all'
        const resp = await client.get(
          `/account/admin_lti13.getIssuers/-/${lmsCode}`
        )
        dispatch({ type: 'updateMessage', message: null })
        dispatch({ type: 'updateTable', tableData: resp.data.issuers })
      } catch (err) {
        if (err.response.status > 205) {
          try {
            dispatch({
              type: 'updateMessage',
              message: `${err.response.statusText}: ${err.response.data.error}`,
            })
          } catch (e) {
            dispatch({
              type: 'updateMessage',
              message: err.response.statusText,
            })
          }
        } else {
          dispatch({ type: 'updateMessage', message: err })
        }
      }
    }
    getPlatforms()
  }, [])

  const lmsCode = match.params.lmsCode
  const isCanvas = lmsCode === 'canvas'
  const allIssuers = currentState.tableData || []
  const canvasIssuers = partitionCanvasIssuers(allIssuers)

  return (
    <>
      <div className="mb-2 go-back-button-left-align">
        <GoBackLink to="/account/admin_lti13/-/" />
      </div>
      {isCanvas ? (
        <>
          <div className="card-myed">
            <h3>Production Issuers For Canvas:</h3>
            <IssuerTable issuers={canvasIssuers.production} lmsCode={lmsCode} />
          </div>
          <div className="card-myed">
            <h3>Test Issuers For Canvas:</h3>
            <IssuerTable issuers={canvasIssuers.test} lmsCode={lmsCode} />
          </div>
        </>
      ) : (
        <div className="card-myed">
          <h3>Issuers for {platformNameMap[lmsCode]}:</h3>
          <IssuerTable issuers={allIssuers} lmsCode={lmsCode} />
        </div>
      )}
    </>
  )
}
